/**
 * What: Starts existing-account sign-in for an unconnected, verified Shopify store.
 * Transport / data-flow: The page lifecycle opens the existing authenticated signed handoff to Web.
 * Invariants: Sign-in needs no admission read; Web still enforces store access before signup/connection effects.
 */
import type { ReactElement } from 'react'
import { usePageLifecycle } from '../hooks/usePageLifecycle'
import { page } from '../pages/launch-admission.page'
import { openShopifyLaunchConnection } from '../launchAdmissionClient'
import { launchAdmissionCopy as copy } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import type { NavigationClient } from '../shopifyNavigation'
import { ShopifyPageContent } from './ShopifyPageContent'
import { TestFeedSignInCard } from './TestFeedSignInCard'

/** Mount keyed by the verified store, never by a contact address or a mutable TestFeed organization. */
export function LaunchAdmissionPage({
  shopify,
  shop,
}: {
  shopify: ShopifyAppBridge & NavigationClient
  shop: string
}): ReactElement {
  const lifecycle = usePageLifecycle(page, `launch-admission:${shop}`, shopify, {})
  return (
    <s-page heading={copy.title} inlineSize="small">
      <ShopifyPageContent>
        <TestFeedSignInCard
          shop={shop}
          lifecycle={lifecycle}
          failed={lifecycle.state.status === 'error'}
          onSignIn={() => lifecycle.run('connect', (signal) => openShopifyLaunchConnection(shopify, shop, signal))}
          onReload={() => globalThis.window?.location.reload()}
        />
      </ShopifyPageContent>
    </s-page>
  )
}
